import { useEffect, useState, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  Button,
  Icon,
  Flex,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  InputGroup,
  Tooltip,
  Spinner,
} from '@chakra-ui/react';
import { FiUpload, FiDownload, FiTrash2, FiFile, FiLogOut, FiInfo } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { userService, documentoService } from '../config/index';
import { User } from '../types/user';
import type { Documento } from '../services/documentoService';

export const Documentos = () => {
  const [user, setUser] = useState<User | null>(null);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [documentoToDelete, setDocumentoToDelete] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const toast = useToast();
  const brandRed = useColorModeValue('red.500', 'red.300');

  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);
        const currentUser = await userService.getCurrentUser();
        setUser(currentUser);
        
        if (currentUser) {
          const documentosData = await documentoService.obtenerDocumentos();
          setDocumentos(documentosData);
        } else {
          // Redirigir al login si no hay usuario
          navigate('/login');
        }
      } catch (error) {
        console.error('Error al inicializar:', error);
        toast({
          title: 'Error',
          description: 'No se pudieron cargar los datos',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    init();
  }, [navigate, toast]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Verificar tipo de archivo
      if (!file.type.includes('pdf') && !file.type.includes('document')) {
        toast({
          title: 'Tipo de archivo no permitido',
          description: 'Solo se permiten archivos PDF y DOCX',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      // Verificar tamaño del archivo (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Archivo demasiado grande',
          description: 'El tamaño máximo permitido es de 5MB',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      setArchivo(file);
      
      // Si no hay nombre, usar el nombre del archivo
      if (!nombre) {
        setNombre(file.name.split('.')[0]);
      }
    }
  };

  const handleUpload = async () => {
    if (!archivo) {
      toast({
        title: 'Selecciona un archivo',
        description: 'Debes seleccionar un archivo para subir',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    if (!nombre.trim()) {
      toast({
        title: 'Nombre requerido',
        description: 'Debes proporcionar un nombre para el documento',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    try {
      setIsUploading(true);
      await documentoService.subirDocumento(archivo, nombre, descripcion);
      
      // Recargar la lista de documentos
      const documentosData = await documentoService.obtenerDocumentos();
      setDocumentos(documentosData);
      
      // Limpiar el formulario
      setArchivo(null);
      setNombre('');
      setDescripcion('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      toast({
        title: 'Documento subido',
        description: 'El documento se ha subido correctamente',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error al subir documento:', error);
      const message = error instanceof Error ? error.message : 'No se pudo subir el documento';
      toast({
        title: 'Error',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (documentoToDelete) {
      try {
        await documentoService.eliminarDocumento(documentoToDelete);
        
        // Actualizar la lista después de eliminar
        const documentosData = await documentoService.obtenerDocumentos();
        setDocumentos(documentosData);
        
        toast({
          title: 'Documento eliminado',
          description: 'El documento se ha eliminado correctamente',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Error al eliminar documento:', error);
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'No se pudo eliminar el documento',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
    onClose();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getFileIcon = (tipo: string) => {
    if (tipo.includes('pdf')) {
      return <Icon as={FiFile} color="red.500" />;
    } else if (tipo.includes('document')) {
      return <Icon as={FiFile} color="blue.500" />;
    }
    return <Icon as={FiFile} />;
  };

  const canDeleteDocument = (documento: Documento): boolean => {
    if (!user) return false;
    return user.role === 'Admin' || documento.userId === user.email;
  };

  // Prevenir la navegación a la URL del documento (ya que no es una URL real)
  const handleClick = (e: React.MouseEvent, action: () => void) => {
    e.preventDefault();
    e.stopPropagation();
    action();
  };

  // Función para descargar un documento
  const handleDownloadDocument = async (documentId: string, fileName: string) => {
    try {
      console.log('Intentando descargar documento con ID:', documentId);
      
      toast({
        title: 'Preparando descarga',
        description: 'Obteniendo el documento...',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      
      // Verificar autenticación antes de descargar
      const currentUser = await userService.getCurrentUser();
      if (!currentUser) {
        console.error("Error: No hay usuario autenticado para descargar documento");
        toast({
          title: 'Error de autenticación',
          description: 'La sesión ha expirado. Por favor, inicia sesión de nuevo.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        // Forzar actualización de estado de autenticación
        window.dispatchEvent(new Event('auth:force-update'));
        return;
      }

      // Importar la función desde documentoService
      const documentData = await documentoService.obtenerDocumentoParaDescargar(documentId);
      console.log('Datos del documento obtenidos:', documentData ? 'OK' : 'NULL');
      
      if (!documentData) {
        toast({
          title: 'Error al descargar',
          description: 'No se pudo obtener el contenido del documento',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      // Asegurarse de que el contenido del documento está en formato correcto
      // (debe ser una cadena data URI que contiene los datos del archivo)
      if (!documentData.content) {
        console.error('El documento no tiene contenido');
        toast({
          title: 'Error al descargar',
          description: 'El documento no tiene contenido',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      // Creamos un enlace temporal para descargar el archivo
      const link = document.createElement('a');
      link.href = documentData.content;
      link.download = fileName || documentData.name;
      
      // Añadir y luego eliminar el enlace para activar la descarga
      document.body.appendChild(link);
      link.click();
      
      // Pequeña pausa antes de eliminar el enlace para asegurar que la descarga se inicie
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
      toast({
        title: 'Descarga iniciada',
        description: 'El documento se está descargando',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error al descargar documento:', error);
      toast({
        title: 'Error al descargar',
        description: 'No se pudo descargar el documento',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box 
      minH="100vh" 
      backgroundImage="url('/silla.jpg')"
      backgroundSize="cover"
      backgroundPosition="center"
      position="relative"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="whiteAlpha.700"
      />
      <Container maxW="container.xl" position="relative" zIndex={1}>
        <VStack spacing={8} align="stretch">
          <Box 
            position="relative"
            bg="whiteAlpha.500"
            p={{ base: 2, md: 3 }}
            borderRadius="xl"
            backdropFilter="blur(10px)"
            display="flex"
            alignItems="center"
            minH={{ base: "60px", md: "70px" }}
          >
            <Box 
              as="img" 
              src="/famma.png" 
              alt="Logo Famma" 
              className="logo" 
              h={{ base: "45px", md: "55px" }}
              objectFit="contain"
              transition="transform 0.2s ease"
              _hover={{ transform: 'scale(1.05)' }}
              ml={{ base: 2, md: 3 }}
              onClick={() => navigate('/')}
              cursor="pointer"
            />
            <Box flex="1" textAlign="center">
              <Heading
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                fontWeight="bold"
                color="brand.600"
                letterSpacing="tight"
                lineHeight="shorter"
              >
                Documentos
              </Heading>
            </Box>
            <Box w={{ base: "45px", md: "55px" }} /> {/* Spacer para balancear el logo */}
          </Box>

          <Box 
            bg="white" 
            p={6} 
            borderRadius="lg" 
            boxShadow="sm"
            mb={6}
          >
            <Flex justify="space-between" align="center">
              <Box>
                <Heading color={brandRed} mb={2}>Gestión de Documentos</Heading>
                <VStack align="flex-start" spacing={1}>
                  <Text color="gray.600">
                    Sube y gestiona tus documentos PDF y DOCX
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    {user?.email}
                  </Text>
                </VStack>
              </Box>
              <HStack spacing={4}>
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                >
                  Volver al panel
                </Button>
                <Button
                  leftIcon={<FiLogOut />}
                  colorScheme="red"
                  variant="ghost"
                  onClick={async () => {
                    try {
                      await userService.logout();
                      navigate('/login');
                    } catch (error) {
                      console.error('Error al cerrar sesión:', error);
                    }
                  }}
                >
                  Cerrar sesión
                </Button>
              </HStack>
            </Flex>
          </Box>

          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
            <Heading size="md" mb={4} color={brandRed}>Subir Nuevo Documento</Heading>
            
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Nombre del documento</FormLabel>
                <Input 
                  placeholder="Nombre descriptivo del documento" 
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Descripción</FormLabel>
                <Textarea 
                  placeholder="Descripción opcional del documento" 
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>
                  Archivo (PDF o DOCX, máx. 5MB)
                  <Tooltip label="Solo se permiten archivos PDF y DOCX con un tamaño máximo de 5MB">
                    <span>
                      <Icon as={FiInfo} ml={1} />
                    </span>
                  </Tooltip>
                </FormLabel>
                <InputGroup>
                  <Input
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    padding={1}
                  />
                </InputGroup>
                {archivo && (
                  <Text fontSize="sm" color="green.500" mt={1}>
                    Archivo seleccionado: {archivo.name} ({formatFileSize(archivo.size)})
                  </Text>
                )}
              </FormControl>
              
              <Button
                leftIcon={<FiUpload />}
                colorScheme="red"
                onClick={handleUpload}
                isLoading={isUploading}
                loadingText="Subiendo..."
                isDisabled={!archivo || !nombre.trim() || isUploading}
              >
                Subir Documento
              </Button>
            </VStack>
          </Box>

          <Box mt={8} bg="white" p={6} borderRadius="lg" boxShadow="sm" overflowX="auto">
            <Heading size="md" mb={6} color={brandRed}>Documentos Disponibles</Heading>
            
            {isLoading ? (
              <Flex justify="center" align="center" py={10}>
                <Spinner size="xl" color={brandRed} />
              </Flex>
            ) : documentos.length === 0 ? (
              <Text textAlign="center" py={10} color="gray.500">
                No hay documentos disponibles
              </Text>
            ) : (
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th width="5%"></Th>
                      <Th width="20%">Nombre</Th>
                      <Th width="30%">Descripción</Th>
                      <Th width="15%">Subido por</Th>
                      <Th width="15%">Fecha</Th>
                      <Th width="10%">Tamaño</Th>
                      <Th width="5%">Acciones</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {documentos.map((documento) => (
                      <Tr key={documento.id}>
                        <Td>{getFileIcon(documento.tipo)}</Td>
                        <Td>
                          <Text
                            color={brandRed}
                            cursor="pointer"
                            fontWeight="medium"
                            onClick={(e) => handleClick(e, () => handleDownloadDocument(documento.id, documento.nombre))}
                            textDecoration="underline"
                          >
                            {documento.nombre}
                          </Text>
                        </Td>
                        <Td>{documento.descripcion}</Td>
                        <Td>{documento.nombreUsuario}</Td>
                        <Td>{documento.fechaSubida}</Td>
                        <Td>{formatFileSize(documento.tamaño)}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <Icon 
                              as={FiDownload} 
                              color="blue.500" 
                              boxSize={5}
                              cursor="pointer"
                              _hover={{ color: 'blue.700' }}
                              onClick={(e) => handleClick(e, () => handleDownloadDocument(documento.id, documento.nombre))}
                            />
                            {canDeleteDocument(documento) && (
                              <Icon 
                                as={FiTrash2} 
                                color="red.500" 
                                boxSize={5}
                                cursor="pointer"
                                _hover={{ color: 'red.700' }}
                                onClick={() => {
                                  setDocumentoToDelete(documento.id);
                                  onOpen();
                                }}
                              />
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </VStack>
      </Container>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Eliminar Documento
            </AlertDialogHeader>

            <AlertDialogBody>
              ¿Estás seguro? Esta acción no se puede deshacer.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
              >
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default Documentos;
